<template>
   <div>
      <div v-if="!loaded">
         <SpinnerOverlay />
      </div>
      <b-container v-if="loaded">
         <QuestionnairesTitle title="Questionnaires" />

         <transition name="fade" mode="out-in">
            <div v-if="displayIntro" key="intro-content">
               <p class="mb-3">
                  This section contains questionnaires we need you to answer, justifying how your
                  efforts pass the
                  <b-link id="four-part-test-link" :to="{name: 'get-started-stage'}"
                     >4-part test</b-link
                  >.
                  <template v-if="!isSME">
                     You can assign these questionnaires to other users if someone else could answer
                     them better.
                  </template>
                  These questionnaires are the final bit of info we need to get you an audit-proof
                  R&D tax credit. So let's get to it!
               </p>

               <div class="d-flex justify-content-end align-items-center mt-4">
                  <b-button
                     id="btn-begin"
                     variant="success"
                     @click="hideIntro"
                     style="font-size: 0.75rem"
                  >
                     Begin
                     <b-icon-arrow-right-short />
                  </b-button>
               </div>
            </div>

            <div v-else key="main-content">
               <ManagerMessage>
                  <template #short>
                     <p v-if="isSectionCompleted" class="mb-0 card-title">
                        This section is complete.
                     </p>
                     <p v-else-if="readyToCompleteSection" class="mb-0 card-title">
                        This section is ready to mark complete.
                     </p>
                     <p v-else-if="questionnaireList.length > 0" class="mb-0 card-title">
                        Some of these questionnaires need answers.
                     </p>
                     <p v-else class="mb-0 card-title">Nothing to do here. Let's move on.</p>
                  </template>

                  <template v-if="!isSectionCompleted && readyToCompleteSection">
                     <h2 class="card-title mb-2 mt-0">This section is ready to mark complete.</h2>
                     <p class="card-description mb-0">
                        All of your questionnaires have been answered. If you're confident all the
                        information is accurate, click <b>Mark Section Complete</b>.
                     </p>
                  </template>
                  <template v-else-if="!isSectionCompleted && questionnaireList.length > 0">
                     <h2 class="card-title mb-2 mt-0">
                        Some of these questionnaires need answers.
                     </h2>
                     <p class="card-description mb-0">
                        These questionnaires provide us with the information we will use to defend
                        your tax credit against audit. Please answer them to the best of your
                        knowledge.
                        <span class="card-description mb-0" v-if="!isSME">
                           Remember, you can assign a questionnaire to another user if someone else
                           is more qualified to answer one or more of its questions.
                        </span>
                     </p>

                     <p class="card-description mb-0">
                        <span>
                           If you need any help answering these, you can always reach me via the
                           Onboard secure messaging feature in the upper right corner.
                        </span>
                     </p>
                  </template>
                  <template v-else-if="questionnaires.length > 0">
                     <h2 class="card-title mb-2 mt-0">This section is complete.</h2>
                     <p class="card-description mb-0">
                        This section has been completed, so as long as everything still looks
                        correct, you're all done here! To make any changes to this section, you'll
                        need to unlock it first.
                     </p>

                     <div class="pt-3 d-flex align-items-center justify-content-end">
                        <b-button
                           v-if="!isSME"
                           id="btn-unlock-questionnaires"
                           class="dark-button mr-2"
                           variant="primary"
                           style="font-size: 0.75rem"
                           @click="unlockSection"
                        >
                           Unlock
                        </b-button>
                        <b-button
                           variant="success"
                           :to="{name: 'Home'}"
                           style="font-size: 0.75rem"
                           class="action-button ml-2"
                           >Dashboard</b-button
                        >
                     </div>
                  </template>
                  <template v-else>
                     <h2 class="card-title mb-2 mt-0">Nothing to do here. Let's move on.</h2>
                     <p class="card-description mb-0">
                        You don't have any questionnaires to complete. Visit the dashboard to see if
                        we still need more info from you.
                     </p>

                     <div class="d-flex justify-content-end">
                        <b-button variant="success" :to="{name: 'Home'}" style="font-size: 0.75rem"
                           >Dashboard</b-button
                        >
                     </div>
                  </template>
               </ManagerMessage>
               <b-card class="card-container">
                  <h4 class="card-title">Questionnaires</h4>
                  <b-table
                     v-if="questionnaireList.length > 0"
                     :fields="fields"
                     sort-by="title"
                     :items="questionnaireList"
                     id="table-questionnaires"
                     class="mb-3 table-container"
                  >
                     <template #cell(title)="data">
                        <label class="table-name-container">{{ data.value }}</label>
                     </template>

                     <template #cell(status)="data">
                        <RdigStatusLabel :status="questionnaireStatus(data.item).status" />
                     </template>

                     <template #cell(actions)="data">
                        <div class="cell-w-buttons d-flex justify-content-end">
                           <b-button
                              :id="`btn-questionnaire-${data.index}`"
                              :variant="questionnaireStatus(data.item).btnVariant"
                              size="sm"
                              class="btn-w-icon no-wrap complete-button py-1"
                              @click="toQuestionnaire(data.item)"
                              :disabled="hasOnlyCompletedPastAssignments(data.item)"
                              style="font-size: 0.75rem"
                           >
                              {{ questionnaireStatus(data.item).btnText }}
                           </b-button>
                        </div>
                     </template>
                  </b-table>
               </b-card>
               <b-alert variant="primary" class="mb-0 my-4" :show="questionnaires.length === 0">
                  You don't have any project questionnaires assigned to you.
               </b-alert>

               <div class="d-flex align-items-center justify-content-between mt-4">
                  <b-button
                     v-if="!displayIntro"
                     variant="primary"
                     class="d-flex align-items-center dark-button ml-2 px-4 view-intro-button"
                     @click="displayIntro = true"
                     style="font-size: 0.75rem"
                  >
                     <b-icon-arrow-left-short class="mr-1" />
                     View Introduction
                  </b-button>

                  <b-button
                     v-if="!isSectionCompleted && !isSME"
                     id="btn-complete-questionnaires"
                     class="action-button ml-auto"
                     variant="success"
                     :disabled="!readyToCompleteSection"
                     @click="completeSection"
                     style="font-size: 0.75rem"
                  >
                     Mark Section Complete <b-icon-check />
                  </b-button>
               </div>
            </div>
         </transition>
      </b-container>
   </div>
</template>

<script>
import {mapGetters} from 'vuex';
import RdigStatusLabel from '../../components/RdigStatusLabel.vue';
import QuestionnairesTitle from '../../components/QuestionnairesTitle.vue';
import ManagerMessage from '@/components/ManagerMessage';
import SpinnerOverlay from '@/views/common/widgets/SpinnerOverlay';

export default {
   components: {QuestionnairesTitle, RdigStatusLabel, ManagerMessage, SpinnerOverlay},

   data() {
      return {
         loaded: false,
         displayIntro: true,
         fields: [
            {key: 'title', sortable: true, thStyle: {width: '55%'}},
            {
               key: 'projectId',
               label: 'Project',
               formatter: (projId) => this.projectName(projId),
               sortByFormatted: true,
            },
            {
               key: 'status',
               class: 'no-wrap',
               formatter: (value, key, item) => this.questionnaireStatus(item).status,
               sortByFormatted: true,
            },
            {key: 'actions', label: ''},
         ],
         StatusType: this.$constants().StatusType,
      };
   },

   computed: {
      ...mapGetters({
         profile: 'profile',
         isSME: 'isSME',
         manager: 'manager',
         clientState: 'clientState',
         activeStudyId: 'companies/activeStudyId',
         uploadCategories: 'uploads/uploadCategories',
         company: 'companies/currentCompany',
         projectMap: 'projects/projectMap',
         previousStage: 'previousStage',
         nextStage: 'nextStage',
         questionnaires: 'questionnaire/questionnaires',
         isSectionCompleted: 'questionnaire/isSectionCompleted',
         readyToCompleteSection: 'questionnaire/readyToCompleteSection',
         hasQuestionnaireAssignment: 'progress/hasQuestionnaireAssignment',
         isActiveStudyInSetupStage: 'companies/isActiveStudyInSetupStage',
      }),

      /** The company ID for the logged in user */
      companyId() {
         return this.profile.companyId;
      },

      /** Has the user seen the intro for this section */
      seenIntro() {
         return this.clientState.sectionIntros.project;
      },

      questionnaireList() {
         return Object.values(this.questionnaires);
      },
   },

   methods: {
      loadData() {
         let requests = [
            this.$store.dispatch('questionnaire/loadQuestionnaires', {companyId: this.companyId}),
            this.$store.dispatch('projects/loadProjects', {companyId: this.companyId}),
         ];
         return this.loadPage(requests);
      },

      /** Lock the Questionnaires section */
      async completeSection() {
         await this.blockingRequest('questionnaire/completeSection', {
            companyId: this.companyId,
         });
      },

      /** Unlock the Questionnaires section */
      async unlockSection() {
         const msg =
            "Unlocking this section may delay your credit calculation. You will have to mark it complete again once you're done.";
         const title = 'Unlock this section?';
         const proceed = await this.$bvModal.msgBoxConfirm(msg, {
            title,
            centered: true,
         });
         if (!proceed) return;

         await this.blockingRequest('questionnaire/unlockSection', {
            companyId: this.companyId,
         });
      },

      /** Hide the intro section */
      hideIntro() {
         if (!this.seenIntro) {
            this.$store.dispatch('setSectionIntro', {
               section: 'project',
               value: true,
            });
         }
         this.displayIntro = false;
      },

      /** Redirect to the selected project questionnaire */
      toQuestionnaire(item) {
         if (item.projectId === null) {
            this.$router.push({
               name: 'customer-company-questionnaire-complete',
               params: {id: this.companyId, questionnaireId: item.id},
            });
         } else {
            this.$router.push({
               name: 'customer-project-questionnaire-complete',
               params: {id: this.companyId, questionnaireId: item.id},
            });
         }
      },

      /** Has the user completed all of their assigned questionnaires? */
      hasOnlyCompletedPastAssignments(questionnaire) {
         let assignments = questionnaire.assignments.filter((q) => q.assignee === this.profile.id);
         return assignments.length > 0 && assignments.every((q) => q.completed);
      },

      projectName(projectId) {
         if (projectId === null) {
            return '(Company-wide)';
         }
         return this.projectMap[projectId].name;
      },

      /** Determine the status of a questionnaire */
      questionnaireStatus(questionnaire) {
         if (questionnaire.completed !== null) {
            return {
               status: this.StatusType.COMPLETE,
               btnText: 'Review',
               btnVariant: 'primary',
            };
         }

         let hasAnswer = false;
         questionnaire.questions.forEach((question) => {
            hasAnswer =
               hasAnswer || (question.answers.length > 0 && question.answers[0].answer !== null);
         });

         if (hasAnswer) {
            return {
               status: this.StatusType.IN_PROGRESS,
               btnText: 'Continue',
               btnVariant: 'primary',
            };
         } else {
            return {
               status: this.StatusType.INCOMPLETE,
               btnText: 'Start',
               btnVariant: 'primary',
            };
         }
      },
   },

   watch: {
      activeStudyId() {
         if (this.isActiveStudyInSetupStage) {
            this.$router.push({name: 'Home'});
            return;
         }
         if (!this.hasQuestionnaireAssignment) {
            this.$router.push({name: 'Home'});
         } else {
            this.loadData();
         }
      },
   },

   async created() {
      try {
         await this.loadData();
      } finally {
         this.displayIntro = !this.seenIntro;
         this.loaded = true;
      }
   },
};
</script>
<style lang="scss" scoped>
.card-container {
   margin-bottom: 25px;
}
.card-title {
   margin-bottom: 25px;
   margin-top: 10px;
   font-size: 16px;
   font-weight: 600;
}

.table-name-container {
   font-weight: 600;
   font-size: 12px;
}
.view-intro-button {
   font-size: 12px;
   background-color: $dark-blue;
}

.card-title {
   font-size: 1.25rem;
   font-weight: 700;
}

.card-description {
   font-size: 0.8125rem;
}

.complete-button {
   font-size: 10px;
   width: 90px;
   background-color: $light-blue;
   display: flex;
   justify-content: center;

   &:hover {
      background-color: $light-blue;
      font-size: 10px;
   }
}

.table-container {
   margin: 15px 0;
   border-top: 2px solid #f5f5f5;
   border-bottom: 2px solid #f5f5f5;
   font-size: 12px;
}
</style>
